import { Middleware } from '@reduxjs/toolkit';

import { getSocket } from './socket';
import { RootState } from '../store';
import { IAuthState } from '../store/AuthSlice';
import {
  addMessage,
  chatDisconnectSocket,
  initiChatConnection,
  onNewConversationCreated,
  onUserChatDisconnected,
  selectConversation,
  setChatConnectionMode,
  setChatLoadMoreNotificationData,
  setConversations,
  setInitiated,
  setMessages,
  setSyncMessages,
  setUserNotificationData,
  setUserStatus,
  syncMessages,
  updateConversationsOnMessageReceived,
  updateNotificationDataOnMessageReceived,
  updateNotificationDataOnSeenMessages,
  updateReceivedConversationTypingData,
} from '../store/V2/ChatSlice';

const chatSocketMiddleware: Middleware = (store) => {
  let socket;

  const globalStore: RootState = store.getState();
  const authState: IAuthState = globalStore.auth;
  const authUser = authState.user;

  const initializeSocket = () => {
    socket.connect();

    socket.on('connect', (data) => {
      store.dispatch(setInitiated(true));
    });

    socket.on('onChatConnectionVerified', (data) => {
      store.dispatch(setChatConnectionMode(data));
    });

    socket.on('disconnect', () => {
      store.dispatch(onUserChatDisconnected());
    });

    socket.on('connect_error', (error) => {
      store.dispatch(setChatConnectionMode(undefined));
    });

    socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    // Other event listeners
    socket.on('sendConversations', (data) => {
      store.dispatch(setConversations(data));

      if (data && data.length === 1) {
        store.dispatch(selectConversation(data[0]));
      }
    });

    socket.on('sendMessages', (data) => store.dispatch(setMessages(data)));

    socket.on('receiveMessage', handleReceiveMessage);

    socket.on('newConversation', handleNewConversation);

    socket.on('userStatus', (data) => store.dispatch(setUserStatus(data)));

    socket.on('receiveUserNotificationData', (data) =>
      store.dispatch(setUserNotificationData(data))
    );

    socket.on('syncMessages', (data) => {
      store.dispatch(setSyncMessages(data));
      store.dispatch(updateNotificationDataOnSeenMessages(data));
    });

    socket.on('receiveConversationTypingData', (data) =>
      store.dispatch(updateReceivedConversationTypingData(data))
    );

    // Recieve loadmore user notification data
    socket.on('receiveUserNotificationDataLoadMore', (data) => {
      store.dispatch(setChatLoadMoreNotificationData(data));
    });
  };

  const handleNewConversation = (conversation) => {
    store.dispatch(onNewConversationCreated(conversation));
    store.dispatch(selectConversation(conversation));
  };

  const handleReceiveMessage = (message) => {
    store.dispatch(addMessage(message));
    store.dispatch(updateNotificationDataOnMessageReceived(message));
    store.dispatch(updateConversationsOnMessageReceived(message));

    if (
      authUser &&
      message.sender._id !== authUser._id &&
      message.status.some(
        (item) => item.user === authUser._id && item.status === 'SAVED'
      )
    ) {
      store.dispatch(syncMessages({ ids: [message._id], status: 'DELIVERED' }));
    }
  };

  const emitEvent = (event, payload) => {
    if (socket && socket.connected) {
      socket.emit(event, payload);
    } else {
      console.warn(`Socket is not connected. Cannot emit event: ${event}`);
    }
  };

  return (next) => (action) => {
    switch (action.type) {
      case initiChatConnection.type:
        if (!socket) {
          socket = getSocket();
          initializeSocket();
        }
        break;

      case chatDisconnectSocket.type:
        if (socket) {
          socket.disconnect();
          socket = null;
        }
        break;

      case 'chat/verifyChatConnection':
      case 'chat/getConversations':
      case 'chat/startConversation':
      case 'chat/sendMessage':
      case 'chat/getMessages':
      case 'chat/syncMessages':
      case 'chat/sendConversationTypingData':
      case 'chat/getConversationsForAdmin':
      case 'chat/chatLoadMoreNotificationData':
        emitEvent(action.type.replace('chat/', ''), action.payload);
        break;

      default:
        break;
    }

    return next(action);
  };
};

export default chatSocketMiddleware;
